import * as amplitude from "@amplitude/analytics-browser";
import { Fade } from "@mui/material";
import { useRequiredContext } from "@redotech/react-util/context";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { allowSupportAiCopilot } from "@redotech/redo-model/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import CircleSpinner from "@redotech/redo-web/circle-spinner.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import { assertNever } from "@redotech/util/type";
import { browserIsMac } from "@redotech/web-util/browser";
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { hotkeyTooltip } from "./message-input/hotkeys";
import * as styles from "./writing-ai-assistant-menu.module.css";

type AiOperation = "suggest" | "polish";

export enum QuillOperation {
  INSERT = "insert",
  REPLACE = "replace",
}

const SHOWN_AI_ENGAGEMENT_TOOLTIP_KEY = "shown_ai_engagement_tooltip";

interface WritingAiAssistantMenuProps {
  conversationId: string;
  draftId?: string;
  messageRespondingToId: string;
  onAiOperationComplete: (content: string, operation: QuillOperation) => void;
  onSuggestResponse?: (text: string) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onHotkeyFunctionReady: (
    hotkeyFunction: (event: KeyboardEvent) => void,
  ) => void;
  proactiveCopilotLoading?: boolean;
}

export const WritingAiAssistantMenu: React.FC<WritingAiAssistantMenuProps> = ({
  conversationId,
  draftId,
  messageRespondingToId,
  onAiOperationComplete,
  onSuggestResponse,
  onLoadingChange,
  onHotkeyFunctionReady,
  proactiveCopilotLoading = false,
}) => {
  const team = useRequiredContext(TeamContext);
  const redoMerchantRpcClient = useRequiredContext(
    RedoMerchantRpcClientContext,
  );
  const client = useRequiredContext(RedoMerchantClientContext);
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const [selectedOperation, setSelectedOperation] =
    useState<AiOperation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMac = browserIsMac();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [aiTooltipOpen, setAiTooltipOpen] = useState(false);

  const canUseAi = allowSupportAiCopilot(team);
  const isAiLoading = isLoading || proactiveCopilotLoading;
  const disabled = isAiLoading || !canUseAi;
  const [showAiEngagementTooltip, setShowAiEngagementTooltip] = useState(false);

  useEffect(() => {
    // wait 2 seconds before showing engagement tooltip
    // show once every 7 days if they haven't tried ai yet
    if (
      !getLocalStorageWithExpiry(SHOWN_AI_ENGAGEMENT_TOOLTIP_KEY) &&
      !team.settings.support?.billing?.ai?.billingAccepted &&
      (team.settings.support?.ai?.trialMessagesRemaining || 0) > 0 &&
      !team.settings.support?.ai?.hasTriedAi
    ) {
      setTimeout(() => {
        setShowAiEngagementTooltip(true);
      }, 1000);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAiOperation = useCallback(
    async (operation: AiOperation) => {
      if (disabled) {
        return;
      }

      setIsLoading(true);
      if (onLoadingChange) {
        onLoadingChange(true);
      }

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          let result;
          if (operation === "suggest") {
            result = await supportRpcClient.generateCopilotResponse({
              conversationId,
              prevMessageId: messageRespondingToId,
              overwrite: true, // User explicitly requested, so regenerate even if cached
            });
            if (result?.text) {
              amplitude.logEvent("generate-copilotSuggestion", {
                conversationId,
              });
              // Use onSuggestResponse if provided, otherwise fall back to direct insertion
              if (onSuggestResponse) {
                onSuggestResponse(result.text);
              } else {
                onAiOperationComplete(result.text, QuillOperation.INSERT);
              }
            }
          } else if (operation === "polish") {
            if (!draftId) {
              return;
            }
            result = await redoMerchantRpcClient.polishMessage({
              draftId,
              conversationId,
            });
            if (result?.polishedResponse) {
              onAiOperationComplete(
                result.polishedResponse,
                QuillOperation.REPLACE,
              );
            }
          } else {
            assertNever(operation);
          }
        } catch (error) {
          console.error("AI operation failed:", error);
        } finally {
          setIsLoading(false);
          if (onLoadingChange) {
            onLoadingChange(false);
          }
          setSelectedOperation(null);
        }
      }, 300);
    },
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      client,
      redoMerchantRpcClient,
      conversationId,
      draftId,
      onAiOperationComplete,
      onLoadingChange,
      isLoading,
    ],
  );

  const optionSelected = useCallback(
    ({ item }: { item: RedoListItem<AiOperation> }) => {
      setSelectedOperation(item.value);
      void handleAiOperation(item.value);
    },
    [handleAiOperation],
  );

  const disabledItems: AiOperation[] = useMemo(() => {
    if (disabled) {
      return ["polish", "suggest"];
    }

    const disabledItems: AiOperation[] = [];
    if (!draftId) {
      disabledItems.push("polish");
    }
    return disabledItems;
  }, [draftId, disabled]);

  const isToolDisabled = useMemo(() => {
    return (operation: AiOperation): boolean => {
      if (operation === "suggest") {
        return disabled;
      }
      if (operation === "polish") {
        return disabled || !draftId;
      }
      return false;
    };
  }, [disabled, draftId]);

  const maybePerformOptionHotkeyAction = useCallback(
    (event: KeyboardEvent) => {
      if (!event.altKey) {
        return;
      }
      if (
        (event.key === "1" || event.key === "¡") &&
        !isToolDisabled("suggest")
      ) {
        event.preventDefault();
        event.stopPropagation();
        void handleAiOperation("suggest");
      }
      if (
        (event.key === "2" || event.key === "™") &&
        !isToolDisabled("polish")
      ) {
        event.preventDefault();
        event.stopPropagation();
        void handleAiOperation("polish");
      }
    },
    [handleAiOperation, isToolDisabled],
  );

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    onHotkeyFunctionReady(maybePerformOptionHotkeyAction);
  }, [onHotkeyFunctionReady, maybePerformOptionHotkeyAction]);

  const IconTrailing = useMemo(
    () =>
      function IconTrailing() {
        return <Flex>{isAiLoading ? <CircleSpinner /> : <AiStarIcon />}</Flex>;
      },
    [isAiLoading],
  );

  if (showAiEngagementTooltip) {
    return (
      <div>
        <Tooltip
          arrow
          onClose={() => {
            // intentionally empty, controlled with x button
          }}
          open={showAiEngagementTooltip && !dropdownOpen}
          placement="top"
          slotProps={{ transition: { timeout: 600 } }}
          slots={{ transition: Fade }}
          subtitle={
            <div className={styles.leftAlign}>
              Did you know that these responses can be automated using our new
              AI Copilot feature?
            </div>
          }
          title={
            <Flex align="center" justify="space-between">
              <div className={styles.leftAlign}>Use AI generated responses</div>
              <RedoButton
                hierarchy="tertiary"
                IconLeading={XIcon}
                onClick={() => {
                  setLocalStorageWithExpiry(
                    SHOWN_AI_ENGAGEMENT_TOOLTIP_KEY,
                    "seen",
                    24 * 7,
                  );
                  setShowAiEngagementTooltip(false);
                }}
              />
            </Flex>
          }
        >
          <span
            onMouseEnter={() => setAiTooltipOpen(true)}
            onMouseLeave={() => setAiTooltipOpen(false)}
          >
            <RedoButton
              data-testid="aiResponseButton"
              disabled={disabled}
              IconTrailing={IconTrailing}
              onClick={() => setDropdownOpen(!dropdownOpen)}
              ref={dropdownButtonRef}
              size="md"
            />
            <RedoListDropdown
              disabledItems={disabledItems}
              dropdownAnchor={dropdownButtonRef.current}
              dropdownOpen={dropdownOpen}
              items={aiOperations}
              itemSelected={optionSelected}
              selectedItems={selectedOperation || undefined}
              setDropdownOpen={setDropdownOpen}
              size="md"
            />
          </span>
        </Tooltip>
      </div>
    );
  }

  return (
    <Tooltip
      arrow
      open={aiTooltipOpen && !dropdownOpen}
      placement="top"
      title={
        !canUseAi
          ? "Enable copilot in the AI support tab"
          : hotkeyTooltip({
              action: "Show",
              code: "1",
              name: "AI actions",
              isMac,
              modifierKey: "alt",
            })
      }
    >
      <span
        onMouseEnter={() => setAiTooltipOpen(true)}
        onMouseLeave={() => setAiTooltipOpen(false)}
      >
        <RedoButton
          data-testid="aiResponseButton"
          disabled={disabled}
          IconTrailing={IconTrailing}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          ref={dropdownButtonRef}
          size="md"
        />
        <RedoListDropdown
          disabledItems={disabledItems}
          dropdownAnchor={dropdownButtonRef.current}
          dropdownOpen={dropdownOpen}
          items={aiOperations}
          itemSelected={optionSelected}
          selectedItems={selectedOperation || undefined}
          setDropdownOpen={setDropdownOpen}
          size="md"
        />
      </span>
    </Tooltip>
  );
};

const getOperationDisplayText = (value: AiOperation): string => {
  switch (value) {
    case "suggest":
      return "Suggest a response";
    case "polish":
      return "Improve writing";
    default:
      assertNever(value);
  }
};

const operationBadgeNumbers: Record<AiOperation, string> = {
  suggest: "1",
  polish: "2",
};

const aiOperations: RedoListItem<AiOperation>[] = [
  {
    id: "suggest",
    value: "suggest",
    type: "text",
    text: getOperationDisplayText("suggest"),
    hotkey: operationBadgeNumbers.suggest,
  },
  {
    id: "polish",
    value: "polish",
    type: "text",
    text: getOperationDisplayText("polish"),
    hotkey: operationBadgeNumbers.polish,
  },
];
